import React, { useEffect } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { useAtomValue } from 'jotai';
import { Outlet } from 'react-router-dom';

import {
  uiRoutes,
  useAccountInfo,
  useOptimizelyFeatureEnabledWhenReady,
} from '@consumer-frontend/nc-api';
import { isProdEnv, redirectTo } from '@consumer-frontend/nc-utils';

import { NcLoadingPage } from '../../../lib/NcLoadingPage';
import { accountIdAtom } from '../../../store';

const TAKE_A_DRAW_FLAG = 'react_take_a_draw_page';

export const NcRequestCashAdvanceRouter = (): React.ReactNode => {
  const { isEnabled, isLoading: isOptimizelyLoading } = useOptimizelyFeatureEnabledWhenReady(TAKE_A_DRAW_FLAG);
  const accountId = useAtomValue(accountIdAtom);
  const { accountInfo, isLoading: isAccountLoading } = useAccountInfo(accountId);
  const isProduction = isProdEnv();

  // For production where the feature is blocked outright, the Optimizely decision can never
  // change the outcome, so we must not wait on its datafile fetch to decide.
  const hasOptimizelyReturned = isProduction || !isOptimizelyLoading;

  const loanNumber = accountInfo?.loanNumber;
  const isReactCashAdvanceEnabled = !isProduction && isEnabled;

  // Once account data has settled (loaded, or errored - `isLoading` from SWR covers both), fall back
  // to the loan-agnostic Legacy account summary if there's no loan to build a loan-scoped URL from.
  const shouldRedirectToLegacy = hasOptimizelyReturned && !isReactCashAdvanceEnabled && !isAccountLoading;
  const legacyRedirectTarget = loanNumber ? uiRoutes.legacyAH.cashAdvance(loanNumber) : uiRoutes.legacyAH.accountSummary;

  // Redirects users to either legacy or react request cash advance page
  useEffect(
    () => {
      if (shouldRedirectToLegacy) {
        datadogRum.addAction('NcRequestCashAdvanceLegacyRedirect', { isFlagEnabled: isEnabled });
        redirectTo(legacyRedirectTarget);
      }
    },
    [
      isEnabled,
      shouldRedirectToLegacy,
      legacyRedirectTarget,
    ],
  );

  if (!hasOptimizelyReturned || !isReactCashAdvanceEnabled) {
    return <NcLoadingPage />;
  }

  return <Outlet />;
};
